import React, { SFC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
    id: {
        display: 'inline-block',
        fontSize: '0.7rem',
        lineHeight: 1.4,
        color: theme.palette.text.disabled,
        fontFamily: 'monospace',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
            color: theme.palette.text.secondary,
        },
    },
}))

interface CopyableSessionIdProps {
    id: string
    className?: string
}

/**
 * Kratke ID nabijania (#prvych 8 znakov UUID) - nenapadne pre bezneho spravcu,
 * klik skopiruje cele UUID do schranky (dohladanie v DB / podpora).
 * Pouziva sa na karte nabijania (SessionCard) aj v tabulke Charging history.
 */
const CopyableSessionId: SFC<CopyableSessionIdProps> = ({ id, className }) => {
    const classes = useStyles()
    const { t } = useTranslation()
    const [copied, setCopied] = useState(false)

    const copy = (event: React.MouseEvent) => {
        // v tabulke / karte nechceme spustit ine akcie riadku
        event.stopPropagation()
        const done = () => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        }
        // fallback pre prehliadace bez clipboard API (napr. stranka bez HTTPS)
        const fallback = () => {
            const textarea = document.createElement('textarea')
            textarea.value = id
            textarea.style.position = 'fixed'
            textarea.style.opacity = '0'
            document.body.appendChild(textarea)
            textarea.select()
            try {
                document.execCommand('copy')
                done()
            } finally {
                document.body.removeChild(textarea)
            }
        }
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(id).then(done, fallback)
        } else {
            fallback()
        }
    }

    if (!id) return null

    return (
        <Tooltip title={(copied ? t('history.sessionIdCopied') : t('history.sessionIdCopy')) as string}>
            <Typography component="span" className={`${classes.id} ${className ?? ''}`} onClick={copy}>
                #{String(id).slice(0, 8)}
            </Typography>
        </Tooltip>
    )
}

export default CopyableSessionId
